import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface CatalogOption {
  readonly catalogId: string;
  readonly catalogCode: string;
  readonly assetName: string;
}

interface UserOption {
  readonly userId: string;
  readonly displayName: string;
  readonly employeeCode: string | null;
}

interface GovernancePosture {
  readonly totalDatasets: number;
  readonly catalogued: number;
  readonly certified: number;
  readonly withLineage: number;
  readonly qualityFailing: number;
  readonly byClassification: Readonly<Record<string, number>>;
}

interface DatasetGovernanceView {
  readonly datasetId: string;
  readonly datasetCode: string;
  readonly datasetName: string;
  readonly classification: string;
  readonly sensitivity: string;
  readonly zone: string;
  readonly ownerId: string | null;
  readonly qualityScore: number | null;
  readonly catalogued: boolean;
  readonly certified: boolean;
  readonly catalogOwnerId: string | null;
  readonly catalogStewardId: string | null;
  readonly hasLineage: boolean;
  readonly upstreamCount: number;
  readonly downstreamCount: number;
  readonly latestQualityResult: string | null;
  readonly failingQualityRules: number;
  readonly governanceComplete: boolean;
}

const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const SENSITIVITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/**
 * M41 — Data Governance Console (§26 / §16). The governance surface over a
 * dataset's posture. Inspect the tenant-wide posture (cataloguing / certification
 * / lineage / quality coverage — BR-01 / BR-02 / BR-04), look up a single
 * dataset's governance view, then update its classification / sensitivity or
 * assign catalog ownership. Viewing needs GOVERNANCE_VIEW; changes need
 * GOVERNANCE_MANAGE.
 */
export function DataGovernanceConsole(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [posture, setPosture] = useState<GovernancePosture | null>(null);
  const [postureError, setPostureError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [datasetId, setDatasetId] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<DatasetGovernanceView | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [classification, setClassification] = useState('INTERNAL');
  const [sensitivity, setSensitivity] = useState('MEDIUM');
  const [catalogId, setCatalogId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [stewardId, setStewardId] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [secDatasetId, setSecDatasetId] = useState('');
  const [secClassification, setSecClassification] = useState('INTERNAL');
  const [secSensitivity, setSecSensitivity] = useState('MEDIUM');
  const [secEncryption, setSecEncryption] = useState(false);
  const [secMasking, setSecMasking] = useState(false);

  const [violDatasetId, setViolDatasetId] = useState('');
  const [violKind, setViolKind] = useState('');
  const [violDetail, setViolDetail] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: GovernancePosture }>('/v1/data-lake/governance/posture', { token })
      .then((r) => { if (alive) { setPosture(r.data); setPostureError(null); } })
      .catch((e) => { if (alive) setPostureError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  useEffect(() => {
    if (!query) { setView(null); return; }
    let alive = true;
    setViewLoading(true);
    apiRequest<{ data: DatasetGovernanceView }>(`/v1/data-lake/governance/dataset/${query}`, { token })
      .then((r) => { if (alive) { setView(r.data); setClassification(r.data.classification); setSensitivity(r.data.sensitivity); setViewError(null); } })
      .catch((e) => { if (alive) { setView(null); setViewError((e as Error).message); } })
      .finally(() => { if (alive) setViewLoading(false); });
    return () => { alive = false; };
  }, [token, query, reload]);

  const lookup = (e: FormEvent): void => {
    e.preventDefault();
    setNotice(null);
    setActionError(null);
    setQuery(datasetId.trim());
  };

  const updateClassification = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!view) return;
    try {
      await apiRequest(`/v1/data-lake/governance/dataset/${view.datasetId}/classification`, {
        method: 'PUT',
        body: { classification, sensitivity },
        token,
      });
      setNotice(t('m41.governance.classificationUpdated'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.governance.notAuthChange'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const assignOwnership = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!catalogId.trim()) { setActionError(t('m41.governance.catalogIdRequired')); return; }
    try {
      await apiRequest(`/v1/data-lake/governance/catalog/${catalogId.trim()}/ownership`, {
        method: 'PUT',
        body: { ownerId: ownerId.trim() || null, stewardId: stewardId.trim() || null },
        token,
      });
      setNotice(t('m41.governance.ownershipAssigned'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.governance.notAuthChange'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const enforceSecurity = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest('/v1/data-lake/governance/security/enforce', {
        method: 'POST',
        body: {
          datasetId: secDatasetId.trim() || null,
          classification: secClassification,
          sensitivity: secSensitivity,
          encryptionApplied: secEncryption,
          maskingApplied: secMasking,
        },
        token,
      });
      setNotice(t('m41.governance.securityCompliant'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.governance.notAuthEnforce'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const reportViolation = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!violKind.trim()) { setActionError(t('m41.governance.violationKindRequired')); return; }
    if (!violDetail.trim()) { setActionError(t('m41.governance.violationDetailRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/governance/security/violation', {
        method: 'POST',
        body: { datasetId: violDatasetId.trim() || null, kind: violKind.trim(), detail: violDetail.trim() },
        token,
      });
      setNotice(t('m41.governance.violationRecorded'));
      setViolKind('');
      setViolDetail('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.governance.notAuthReport'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="data-governance-console">
      <h1>{t('m41.governance.title')}</h1>

      {postureError && <p role="alert" aria-label="posture-error">{postureError}</p>}
      {loading ? <p>{t('common.loading')}</p> : posture && (
        <section aria-label="governance-posture">
          <h2>{t('m41.governance.posture')}</h2>
          <ul>
            <li aria-label="posture-total">{t('m41.common.postureTotal', { n: posture.totalDatasets })}</li>
            <li aria-label="posture-catalogued">{t('m41.common.postureCatalogued', { n: posture.catalogued })}</li>
            <li aria-label="posture-certified">{t('m41.common.postureCertified', { n: posture.certified })}</li>
            <li aria-label="posture-lineage">{t('m41.common.postureLineage', { n: posture.withLineage })}</li>
            <li aria-label="posture-quality-failing">{t('m41.common.postureQualityFailing', { n: posture.qualityFailing })}</li>
          </ul>
          <ul aria-label="posture-classification">
            {Object.entries(posture.byClassification).map(([k, v]) => (
              <li key={k} aria-label={`classification-${k}`}>{k}: {v}</li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="governance-lookup">
        <h2>{t('m41.governance.datasetGovernance')}</h2>
        <form aria-label="lookup-form" onSubmit={lookup}>
          <label>{t('m41.common.datasetId')}<input aria-label="field-dataset-id" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-lookup">{t('m41.governance.load')}</Button>
        </form>
      </section>

      {viewError && <p role="alert" aria-label="view-error">{viewError}</p>}
      {viewLoading && <p>{t('common.loading')}</p>}

      {view && (
        <>
          <section aria-label="dataset-governance-view">
            <h2>{view.datasetCode} — {view.datasetName}</h2>
            <ul>
              <li aria-label="view-classification">{t('m41.governance.viewClassification', { value: view.classification })}</li>
              <li aria-label="view-sensitivity">{t('m41.governance.viewSensitivity', { value: view.sensitivity })}</li>
              <li aria-label="view-owner">{t('m41.governance.viewOwner', { value: view.ownerId ?? t('m41.governance.unassigned') })}</li>
              <li aria-label="view-catalogued">{t('m41.governance.viewCatalogued', { value: view.catalogued ? t('common.yes') : t('common.no') })}</li>
              <li aria-label="view-certified">{t('m41.governance.viewCertified', { value: view.certified ? t('common.yes') : t('common.no') })}</li>
              <li aria-label="view-lineage">{t('m41.governance.viewLineage', { value: view.hasLineage ? t('m41.governance.lineageYes', { up: view.upstreamCount, down: view.downstreamCount }) : t('common.no') })}</li>
              <li aria-label="view-quality">{t('m41.governance.viewQuality', { result: view.latestQualityResult ?? '—', failing: view.failingQualityRules })}</li>
              <li aria-label="view-complete">{t('m41.governance.viewComplete', { value: view.governanceComplete ? t('common.yes') : t('common.no') })}</li>
            </ul>
          </section>

          <section aria-label="governance-classification">
            <h2>{t('m41.governance.updateClassification')}</h2>
            <form aria-label="classification-form" onSubmit={(e) => void updateClassification(e)}>
              <label>{t('m41.common.classification')}
                <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
                  {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label>{t('m41.governance.sensitivity')}
                <select aria-label="field-sensitivity" value={sensitivity} onChange={(e) => setSensitivity(e.target.value)}>
                  {SENSITIVITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <Button variant="primary" type="submit" aria-label="submit-classification">{t('m41.governance.update')}</Button>
            </form>
          </section>
        </>
      )}

      <section aria-label="governance-ownership">
        <h2>{t('m41.governance.ownership')}</h2>
        <form aria-label="ownership-form" onSubmit={(e) => void assignOwnership(e)}>
          <RecordPicker<CatalogOption>
            endpoint="/v1/data-lake/catalog?pageSize=500"
            idField="catalogId"
            label={t('m41.governance.catalogId')}
            testId="field-catalog-id"
            value={catalogId}
            optionLabel={(c) => `${c.assetName} (${c.catalogCode})`}
            onPick={setCatalogId}
          />
          <RecordPicker<UserOption>
            endpoint="/v1/users?limit=200"
            idField="userId"
            label={t('m41.governance.ownerId')}
            testId="field-owner-id"
            value={ownerId}
            optionLabel={(u) => personLabel(u.displayName, u.employeeCode) ?? u.displayName}
            onPick={setOwnerId}
          />
          <RecordPicker<UserOption>
            endpoint="/v1/users?limit=200"
            idField="userId"
            label={t('m41.governance.stewardId')}
            testId="field-steward-id"
            value={stewardId}
            optionLabel={(u) => personLabel(u.displayName, u.employeeCode) ?? u.displayName}
            onPick={setStewardId}
          />
          <Button variant="primary" type="submit" aria-label="submit-ownership">{t('m41.governance.assign')}</Button>
        </form>
      </section>

      <section aria-label="governance-security-enforce">
        <h2>{t('m41.governance.securityCheck')}</h2>
        <form aria-label="security-enforce-form" onSubmit={(e) => void enforceSecurity(e)}>
          <label>{t('m41.governance.datasetIdOptional')}<input aria-label="field-security-dataset-id" value={secDatasetId} onChange={(e) => setSecDatasetId(e.target.value)} /></label>
          <label>{t('m41.common.classification')}
            <select aria-label="field-security-classification" value={secClassification} onChange={(e) => setSecClassification(e.target.value)}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m41.governance.sensitivity')}
            <select aria-label="field-security-sensitivity" value={secSensitivity} onChange={(e) => setSecSensitivity(e.target.value)}>
              {SENSITIVITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.governance.encryptionApplied')}<input aria-label="field-security-encryption" type="checkbox" checked={secEncryption} onChange={(e) => setSecEncryption(e.target.checked)} /></label>
          <label>{t('m41.governance.maskingApplied')}<input aria-label="field-security-masking" type="checkbox" checked={secMasking} onChange={(e) => setSecMasking(e.target.checked)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-security-enforce">{t('m41.governance.checkCompliance')}</Button>
        </form>
      </section>

      <section aria-label="governance-security-violation">
        <h2>{t('m41.governance.reportViolation')}</h2>
        <form aria-label="security-violation-form" onSubmit={(e) => void reportViolation(e)}>
          <label>{t('m41.governance.datasetIdOptional')}<input aria-label="field-violation-dataset-id" value={violDatasetId} onChange={(e) => setViolDatasetId(e.target.value)} /></label>
          <label>{t('m41.governance.kind')}<input aria-label="field-violation-kind" value={violKind} onChange={(e) => setViolKind(e.target.value)} /></label>
          <label>{t('m41.governance.detail')}<input aria-label="field-violation-detail" value={violDetail} onChange={(e) => setViolDetail(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-security-violation">{t('m41.governance.recordViolation')}</Button>
        </form>
      </section>

      {actionError && <p role="alert" aria-label="governance-action-error">{actionError}</p>}
      {notice && <p aria-label="governance-notice">{notice}</p>}
    </main>
  );
}
